import { Component, EventEmitter, forwardRef, HostBinding, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { PropertyMlsOption } from '@app-services/property-search/property-search.type';
import { EntityType } from '@app/enum/entity-type.enum';
import { RealEstateService } from '@app-services/realEstate.service';
import { PropertySearchService } from '@app-services/property-search/property-search.service';
import { Subject } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';
import { AvailableIntegration } from '@app-model/integration';
import { coerceBoolean } from '@app-shared/decorators/coerce-boolean.decorator';

@Component({
	selector: 'app-mls-select',
	templateUrl: './mls-select.component.html',
	styleUrls: ['./mls-select.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => MlsSelectComponent),
			multi: true
		}
	]
})
export class MlsSelectComponent implements OnInit, OnDestroy, ControlValueAccessor {
	@Input() entityType?: EntityType;
	@Input() entityId?: string;
	@Input() hiddenWhenOnlyOneOption = true;
	@coerceBoolean() @Input() hiddenWhileLoading = false;
	@coerceBoolean() @Input() disabled = false;
	@Input() placeholderLabel = 'searchProperties.filters.chooseMls';
	@Input() placeholderFallback = 'searchProperties.filters.chooseMlsPlaceholder';
	@Input() emptyLabel = 'searchProperties.filters.chooseMlsEmpty';
	@Input() panelClass: string | string[];
	@Input() appearance: 'fill' | 'outline' | 'standard' | 'legacy' = 'outline';

	@Output() selectionChange = new EventEmitter<MatSelectChange>();
	@Output() optionsLoaded = new EventEmitter<PropertyMlsOption[]>();

	options: PropertyMlsOption[] = [];
	isLoading = false;
	value: string | null = null;

	@HostBinding('class.outline-styling')
	get hostOutlineStyling(): boolean {
		return this.appearance === 'outline';
	}
	@HostBinding('class.legacy-styling')
	get hostLegacyStyling(): boolean {
		return this.appearance === 'legacy';
	}

	private destroy$ = new Subject<void>();
	private onChange: (v: any) => void = () => {};
	private onTouched: () => void = () => {};
	private pendingValue: string | null = null;
	/**
	 * When a non-null value is written via ControlValueAccessor, treat it as an explicit value
	 * that should not be overridden by auto-selection. Null/undefined writes are considered
	 * non-explicit and will allow auto-selection of the first option.
	 */
	private explicitValueSet = false;

	constructor(private realEstateService: RealEstateService, private propertySearchService: PropertySearchService) {}

	ngOnInit(): void {
		this.loadOptions();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private loadOptions(): void {
		this.isLoading = true;

		const request$ =
			this.entityType && this.entityId
				? this.realEstateService
						.getAvailableIntegrations(this.entityType, this.entityId)
						.pipe(map(integrations => this.mapToMlsOptions(integrations)))
				: this.propertySearchService
						.getAvailableIntegrations()
						.pipe(map(integrations => this.mapToMlsOptions(integrations)));

		request$.pipe(takeUntil(this.destroy$)).subscribe({
			next: options => {
				this.options = options;
				this.isLoading = false;
				this.optionsLoaded.emit(options);

				// If there was a pending value, apply it now that options are loaded
				if (this.pendingValue !== null) {
					const optionExists = options.some(opt => opt.integrationConfigId === this.pendingValue);
					if (optionExists) {
						this.value = this.pendingValue;
						this.onChange(this.value);
					}
					this.pendingValue = null;
					return;
				}

				// If no explicit value was provided via writeValue, auto-select the first option
				if (!this.explicitValueSet && !this.value && this.options.length > 0) {
					const first = this.options[0].integrationConfigId;
					this.value = first;
					this.onChange(this.value);
					this.onTouched();
				}
			},
			error: () => {
				this.options = [];
				this.isLoading = false;
				this.optionsLoaded.emit([]);
				this.pendingValue = null;
			}
		});
	}

	private mapToMlsOptions(integrations: AvailableIntegration[]): PropertyMlsOption[] {
		return integrations.map(integration => ({
			integrationConfigId: integration.id,
			provider: integration.providerName,
			token: integration.mlsId,
			name: integration.providerName
		}));
	}

	writeValue(obj: any): void {
		const newValue = obj ?? null;

		// consider non-null writes as explicit (don't override with auto-selection)
		this.explicitValueSet = newValue !== null && newValue !== undefined;

		// If options are already loaded, apply the value immediately
		if (this.options.length > 0) {
			const optionExists = this.options.some(opt => opt.integrationConfigId === newValue);
			this.value = optionExists ? newValue : null;
		} else {
			// Store the value to be applied once options are loaded
			this.pendingValue = newValue;
			this.value = newValue;
		}
	}

	registerOnChange(fn: any): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
	}

	onSelect(event: MatSelectChange): void {
		this.value = event.value ?? null;
		this.onChange(this.value);
		this.selectionChange.emit(event);
	}

	get selectedOption(): PropertyMlsOption | null {
		if (!this.value) return null;
		return this.options?.find(o => o.integrationConfigId === this.value) ?? null;
	}
}
